import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ChevronDown, BadgeCheck } from "lucide-react";
import { Reveal } from "@/components/Motion";
import api from "@/lib/api";
import { usePageMeta } from "@/lib/seo";
import { cityFilterOptions, displayCity, profileCities } from "@/lib/city";
import { fmtHeight } from "@/lib/units";

export default function Models() {
  usePageMeta({
    title: "Talent Board — Fashion Models | Solis Casting",
    description: "Scout the board: approved fashion models with full stat profiles, portfolios and reels — Paris, Milan, New York and beyond.",
    url: `${window.location.origin}/models`,
  });
  const [models, setModels] = useState([]);
  const [city, setCity] = useState("All");
  const [gender, setGender] = useState("All");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/models").then((r) => setModels(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const cities = useMemo(() => ["All", ...cityFilterOptions(models)], [models]);
  const filtered = models.filter(
    (m) => (city === "All" || profileCities(m.city).includes(city)) && (gender === "All" || m.gender === gender)
  );

  return (
    <div className="px-6 sm:px-10 py-24" data-testid="models-page">
      <Reveal>
        <p className="overline-label mb-4">The Directory</p>
        <h1 className="font-display font-light text-5xl sm:text-7xl tracking-tighter mb-16">
          Talent <span className="italic text-accent">Board</span>
        </h1>
      </Reveal>

      <div className="flex flex-wrap items-center gap-x-10 gap-y-4 mb-16 border-y border-white/10 py-6" data-testid="models-filters">
        {[
          { label: "City", value: city, set: setCity, options: cities, tid: "model-city-select" },
          { label: "Gender", value: gender, set: setGender, options: ["All", "Female", "Male"], tid: "model-gender-select" },
        ].map(({ label, value, set, options, tid }) => (
          <div key={label} className="flex items-center">
            <span className="overline-label !text-[10px] mr-4">{label}</span>
            <div className="relative">
              <select
                data-testid={tid}
                value={value}
                onChange={(e) => set(e.target.value)}
                className="appearance-none bg-background border border-white/20 hover:border-white/60 transition-colors duration-300 font-grotesk text-xs uppercase tracking-[0.15em] pl-4 pr-10 py-3 outline-none cursor-pointer"
              >
                {options.map((o) => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
              <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-white/50" />
            </div>
          </div>
        ))}
      </div>

      {loading ? (
        <p className="overline-label" data-testid="models-loading">Loading the board…</p>
      ) : filtered.length === 0 ? (
        <p className="font-display italic text-3xl text-white/40" data-testid="models-empty">No faces match — widen the lens.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10" data-testid="models-grid">
          {filtered.map((m, i) => (
            <Reveal key={m.id} delay={(i % 4) * 0.06}>
              <Link to={`/models/${m.slug || m.id}`} data-testid={`model-card-${m.id}`} className="group block bg-background h-full">
                <div className="aspect-[3/4] overflow-hidden relative">
                  <img
                    src={m.photo_url?.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${m.photo_url}` : m.photo_url}
                    alt={m.name}
                    loading={i < 4 ? "eager" : "lazy"}
                    decoding="async"
                    className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
                  />
                  {m.standard_met && (
                    <span
                      data-testid={`model-card-standard-${m.id}`}
                      className="absolute top-3 right-3 font-grotesk text-[9px] uppercase tracking-[0.18em] border border-accent bg-background/85 backdrop-blur text-accent px-2.5 py-1.5 inline-flex items-center gap-1.5"
                    >
                      <BadgeCheck size={11} /> Standard met
                    </span>
                  )}
                </div>
                <div className="p-5">
                  <div className="flex items-end justify-between">
                    <p className="font-display text-2xl">{m.name}</p>
                    <ArrowUpRight size={18} className="text-white/30 group-hover:text-accent transition-colors" />
                  </div>
                  <p className="overline-label !text-[10px] mt-2">{displayCity(m.city)} · {fmtHeight(m.height_cm)} · {m.gender}</p>
                  <div className="flex flex-wrap gap-2 mt-4">
                    {(m.categories || []).map((cat) => (
                      <span key={cat} className="font-grotesk text-[10px] uppercase tracking-[0.15em] border border-white/15 px-2 py-1 text-white/60">
                        {cat}
                      </span>
                    ))}
                  </div>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      )}
    </div>
  );
}
